/**
 * EmployeeListPage — HR dolgozók lista oldal
 * DataTable (shared @kgc/ui foundation) + "+" gomb + sor kattintásra detail
 */

import { useTranslation } from '@kgc/i18n';
import { Button, Card, CardContent, CardHeader, DataTable, Input } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Employee } from '../api/hr-api';
import { useEmployees } from '../hooks/useEmployees';

export function EmployeeListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data: employees = [], isLoading, error } = useEmployees();

  const filtered = employees.filter(emp => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (emp.userName ?? emp.userId).toLowerCase().includes(q) ||
      (emp.userEmail ?? '').toLowerCase().includes(q) ||
      (emp.tajNumber ?? '').includes(q)
    );
  });

  const columns = useMemo<ColumnDef<Employee>[]>(
    () => [
      {
        id: 'name',
        header: t('hr.employee.name'),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.userName ?? row.original.userId}</span>
        ),
      },
      {
        id: 'email',
        header: t('hr.employee.email'),
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.userEmail ?? '–'}</span>
        ),
      },
      {
        id: 'employmentType',
        header: t('hr.employee.employmentType'),
        cell: ({ row }) =>
          t(
            `hr.employee.employmentTypes.${row.original.employmentType.toLowerCase().replace('_', '')}`
          ),
      },
      {
        id: 'weeklyHours',
        header: t('hr.employee.weeklyHours'),
        cell: ({ row }) => `${row.original.weeklyHours} h`,
      },
      {
        id: 'grossSalary',
        header: t('hr.employee.grossSalary'),
        cell: ({ row }) => `${row.original.grossSalary.toLocaleString('hu-HU')} Ft`,
      },
      {
        id: 'contractStart',
        header: t('hr.employee.contractStart'),
        cell: ({ row }) => new Date(row.original.contractStart).toLocaleDateString('hu-HU'),
      },
    ],
    [t]
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('hr.employee.title')}</h1>
        <Button onClick={() => navigate('/hr/employees/new')}>+ {t('hr.employee.create')}</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filtered}
            isLoading={isLoading}
            error={error ? t('common.error') : null}
            emptyMessage={t('hr.employee.noEmployees')}
            getRowId={emp => emp.id}
            onRowClick={emp => navigate(`/hr/employees/${emp.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
